import React, { useEffect, useRef, useCallback } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

export type ChatFeedProps = React.PropsWithChildren<{}>

/** Threshold in pixels — if the user is within this distance of the bottom, auto-scroll. */
const NEAR_BOTTOM_THRESHOLD = 150

export function ChatFeed(props: ChatFeedProps) {
  const { children } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollBehavior = useRef<ScrollBehavior>('auto')
  const isNearBottomRef = useRef(true)

  const checkIfNearBottom = useCallback(() => {
    const el = containerRef.current
    if (!el) return true
    const { scrollTop, scrollHeight, clientHeight } = el
    return scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_THRESHOLD
  }, [])

  // Track scroll position to know if user has scrolled up
  const handleScroll = useCallback(() => {
    isNearBottomRef.current = checkIfNearBottom()
  }, [checkIfNearBottom])

  useEffect(() => {
    if (bottomRef.current && isNearBottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: scrollBehavior.current })
      if (scrollBehavior.current !== 'smooth') scrollBehavior.current = 'smooth'
    }
  }, [children])

  return (
    <Box ref={containerRef} onScroll={handleScroll} sx={{ height: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
      <Stack spacing={1} sx={{ height: '100%', py: '80px' }}>
        {children}
        <Box ref={bottomRef} />
      </Stack>
    </Box>
  )
}

export default ChatFeed
